<template>
  <div
    class="group flex gap-3 px-4 py-3 transition-colors hover:bg-theme-surface-raised/70"
    :class="{ 'bg-theme-brand/5': !isRead }"
    data-test="notification-item"
    :data-notification-id="notification.id"
    :data-read="isRead ? 'true' : 'false'"
  >
    <!-- Severity dot -->
    <div class="shrink-0 pt-1.5">
      <span class="inline-block w-2 h-2 rounded-full" :class="severityClass" />
    </div>

    <!-- Body -->
    <div class="flex-1 min-w-0">
      <div class="flex items-baseline gap-2">
        <p
          class="truncate text-sm font-medium text-theme-text"
          :class="{ 'font-semibold': !isRead }"
          data-test="notification-item-title"
        >
          {{ title }}
        </p>
        <span class="shrink-0 text-xs text-theme-text-muted">{{ relativeTime }}</span>
      </div>
      <p class="mt-0.5 line-clamp-2 text-sm text-theme-text-secondary">{{ body }}</p>
      <div v-if="ctaUrl" class="mt-1.5 flex items-center gap-3">
        <router-link
          :to="ctaUrl"
          class="text-xs font-medium text-theme-brand underline-offset-4 hover:text-theme-brand-hover hover:underline"
          @click="onCtaClick"
        >
          {{ ctaLabel || $t('notifications.common.open') }}
        </router-link>
      </div>
    </div>

    <!-- Row actions -->
    <div
      class="shrink-0 flex items-start gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
    >
      <AppButton
        v-if="!isRead"
        type="button"
        variant="ghost"
        tone="primary"
        size="xs"
        icon-only
        :icon="mdiEmailOpenOutline"
        :title="$t('notifications.common.mark_read')"
        :aria-label="$t('notifications.common.mark_read')"
        data-test="notification-item-mark-read"
        @click.stop="$emit('mark-read', notification.id)"
      />
      <AppButton
        type="button"
        variant="ghost"
        tone="danger"
        size="xs"
        icon-only
        :icon="mdiTrashCanOutline"
        :title="$t('notifications.common.delete')"
        :aria-label="$t('notifications.common.delete')"
        data-test="notification-item-delete"
        @click.stop="$emit('delete', notification.id)"
      />
    </div>
  </div>
</template>

<script>
  import { mdiEmailOpenOutline, mdiTrashCanOutline } from '@mdi/js';
  import { computed, defineComponent } from 'vue';

  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import { useNotificationStrings } from '@/features/notifications/composables/useNotificationStrings';

  const SEVERITY_CLASS = {
    info: 'bg-theme-info',
    warning: 'bg-theme-warning',
    error: 'bg-theme-danger',
    critical: 'bg-theme-danger ring-2 ring-theme-danger/30',
  };

  export default defineComponent({
    name: 'NotificationItem',
    components: { AppButton },
    props: {
      notification: { type: Object, required: true },
    },
    emits: ['mark-read', 'delete', 'cta-click'],
    setup(props, { emit }) {
      const { title, body, ctaLabel, ctaUrl, relativeTime } = useNotificationStrings(
        () => props.notification
      );
      const isRead = computed(() => Boolean(props.notification?.read_at));
      const severityClass = computed(
        () => SEVERITY_CLASS[props.notification?.severity] || SEVERITY_CLASS.info
      );
      function onCtaClick() {
        emit('cta-click', props.notification);
        // Marking the item read when the user navigates is the conventional UX.
        if (!props.notification?.read_at) {
          emit('mark-read', props.notification.id);
        }
      }
      return {
        title,
        body,
        ctaLabel,
        ctaUrl,
        relativeTime,
        isRead,
        severityClass,
        onCtaClick,
        mdiEmailOpenOutline,
        mdiTrashCanOutline,
      };
    },
  });
</script>
